import * as XLSX from "xlsx";
import { parsePaymentTerms } from "@/lib/backtest/paymentTermsParser";
import { lookupTermsCode } from "@/lib/par/termsCodeMap";

export interface PARInvoiceRow {
  db_id?: string; // Supabase row UUID — set when loaded from DB, undefined for CSV-parsed rows
  vendor_name: string;
  vendor_number: string;
  invoice_number: string;
  description: string;
  status: string;
  payment_status: string;
  terms: string;
  invoiced_date: string;
  due_date: string;
  paid_date: string;
  amount_invoiced: number;
  amount_paid: number;
  amount_discount: number;
  days_paid_early: number;
  invoice_year: number;
  early_pay_due_date: string;
  discount_pct: number | null;
  potential_savings: number;
}

export interface VendorSummary {
  vendor_name: string;
  vendor_number: string;
  invoice_count: number;
  total_invoiced: number;
  total_paid: number;
  total_discount: number;
  terms_used: string[];
  avg_days_paid_early: number;
  earliest_invoice_date: string;
  latest_invoice_date: string;
  earliest_due_date: string;
  latest_due_date: string;
  invoice_numbers: string[];
  /** Total potential savings using each invoice's actual terms */
  total_potential_savings: number;
  /**
   * Additional savings achievable if ALL of this vendor's invoices used the
   * best (highest) discount_pct seen across that vendor's invoices.
   * opportunity = potential_at_best_terms − total_potential_savings
   */
  opportunity: number;
  /** opportunity as a fraction of total_potential_savings (0–1). NaN when potential=0. */
  opportunity_pct: number;
  /** The best discount_pct found across this vendor's invoices (null if none). */
  best_discount_pct: number | null;
}

/**
 * Parse an XLSX or CSV file of AP invoice details into typed rows.
 * Accepts a File object and returns parsed rows + vendor summaries.
 */
export async function parseInvoiceFile(file: File): Promise<{
  rows: PARInvoiceRow[];
  vendors: VendorSummary[];
}> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array", cellDates: true });

  let dataSheet: XLSX.WorkSheet | null = null;
  for (const name of wb.SheetNames) {
    const sheet = wb.Sheets[name];
    const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
    if (json.length > 0) {
      const firstRow = json[0];
      const keys = Object.keys(firstRow);
      if (
        keys.some((k) => k.toLowerCase().includes("vendor name")) ||
        keys.some((k) => k.toLowerCase().includes("invoice number"))
      ) {
        dataSheet = sheet;
        break;
      }
    }
  }

  if (!dataSheet) {
    let maxRows = 0;
    for (const name of wb.SheetNames) {
      const sheet = wb.Sheets[name];
      const json = XLSX.utils.sheet_to_json(sheet);
      if (json.length > maxRows) {
        maxRows = json.length;
        dataSheet = sheet;
      }
    }
  }

  if (!dataSheet) throw new Error("No data sheet found");

  const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(dataSheet, { defval: "" });

  const rows: PARInvoiceRow[] = rawRows
    .map((r) => {
      const vendorName = findCol(r, ["vendor name"]);
      const vendorNumber = findCol(r, ["vendor number"]);
      if (!vendorName && !vendorNumber) return null;

      const terms = findCol(r, ["terms"]);
      const invoicedDate = findCol(r, ["invoiced date"]);
      const amountInvoiced = parseNum(r, ["amount invoiced"]);

      const mappedTerms = lookupTermsCode(terms);
      const parsedTerms = mappedTerms ? null : parsePaymentTerms(terms);

      const discountPct = mappedTerms?.discountPct ?? parsedTerms?.discountPct ?? null;
      const discountDays = mappedTerms?.discountDays ?? parsedTerms?.discountDays ?? null;

      let earlyPayDueDate = "";
      if (invoicedDate && discountDays != null) {
        try {
          const d = new Date(invoicedDate);
          d.setDate(d.getDate() + discountDays);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          earlyPayDueDate = `${y}-${m}-${day}`;
        } catch {
          earlyPayDueDate = "";
        }
      }

      const potentialSavings = discountPct != null ? (amountInvoiced * discountPct) / 100 : 0;

      return {
        vendor_name: vendorName,
        vendor_number: vendorNumber,
        invoice_number: findCol(r, ["invoice number"]),
        description: findCol(r, ["description"]),
        status: findCol(r, ["status"]),
        payment_status: findCol(r, ["payment status"]),
        terms,
        invoiced_date: invoicedDate,
        due_date: findCol(r, ["due date"]),
        paid_date: findCol(r, ["paid date"]),
        amount_invoiced: amountInvoiced,
        amount_paid: parseNum(r, ["amount paid"]),
        amount_discount: parseNum(r, ["amount discount"]),
        days_paid_early: parseNum(r, ["days paid early"]),
        invoice_year: parseNum(r, ["invoice year"]),
        early_pay_due_date: earlyPayDueDate,
        discount_pct: discountPct,
        potential_savings: potentialSavings,
      } as PARInvoiceRow;
    })
    .filter(Boolean) as PARInvoiceRow[];

  const vendors = buildVendorSummaries(rows);

  return { rows, vendors };
}

/**
 * Build vendor summaries from a list of invoice rows.
 * Exported so it can be re-run on filtered subsets.
 */
export function buildVendorSummaries(rows: PARInvoiceRow[]): VendorSummary[] {
  const vendorMap = new Map<string, PARInvoiceRow[]>();
  for (const row of rows) {
    const key = row.vendor_name || row.vendor_number;
    if (!vendorMap.has(key)) vendorMap.set(key, []);
    vendorMap.get(key)!.push(row);
  }

  const vendors: VendorSummary[] = Array.from(vendorMap.entries()).map(([key, invoices]) => {
    const termsSet = new Set(invoices.map((i) => i.terms).filter(Boolean));
    const invoiceDates = invoices
      .map((i) => i.invoiced_date)
      .filter(Boolean)
      .sort();
    const dueDates = invoices
      .map((i) => i.due_date)
      .filter(Boolean)
      .sort();
    const totalInvoiced = invoices.reduce((s, i) => s + (i.amount_invoiced || 0), 0);
    const totalPotentialSavings = invoices.reduce((s, i) => s + (i.potential_savings || 0), 0);

    // Find the best (highest) discount_pct seen for this vendor
    const bestDiscountPct = invoices.reduce<number | null>((best, i) => {
      if (i.discount_pct == null) return best;
      return best == null ? i.discount_pct : Math.max(best, i.discount_pct);
    }, null);

    // Potential savings if every invoice used the best discount_pct
    const potentialAtBestTerms =
      bestDiscountPct != null ? (totalInvoiced * bestDiscountPct) / 100 : 0;

    const opportunity = potentialAtBestTerms - totalPotentialSavings;
    const opportunityPct = totalPotentialSavings > 0 ? opportunity / totalPotentialSavings : 0;

    return {
      vendor_name: invoices[0].vendor_name || key,
      vendor_number: invoices[0].vendor_number,
      invoice_count: invoices.length,
      total_invoiced: totalInvoiced,
      total_paid: invoices.reduce((s, i) => s + (i.amount_paid || 0), 0),
      total_discount: invoices.reduce((s, i) => s + (i.amount_discount || 0), 0),
      terms_used: Array.from(termsSet),
      avg_days_paid_early: (() => {
        const valid = invoices.filter(
          (i) => i.days_paid_early !== 0 && Math.abs(i.days_paid_early) <= 100
        );
        return valid.length > 0
          ? valid.reduce((s, i) => s + i.days_paid_early, 0) / valid.length
          : 0;
      })(),
      earliest_invoice_date: invoiceDates[0] || "",
      latest_invoice_date: invoiceDates[invoiceDates.length - 1] || "",
      earliest_due_date: dueDates[0] || "",
      latest_due_date: dueDates[dueDates.length - 1] || "",
      invoice_numbers: invoices.map((i) => i.invoice_number).filter(Boolean),
      total_potential_savings: totalPotentialSavings,
      opportunity,
      opportunity_pct: opportunityPct,
      best_discount_pct: bestDiscountPct,
    };
  });

  vendors.sort((a, b) => b.total_invoiced - a.total_invoiced);
  return vendors;
}

function findCol(row: Record<string, unknown>, needles: string[]): string {
  for (const key of Object.keys(row)) {
    const lower = key.toLowerCase();
    if (needles.some((n) => lower.includes(n))) {
      const val = row[key];
      if (val instanceof Date && !isNaN(val.getTime())) {
        const y = val.getFullYear();
        const m = String(val.getMonth() + 1).padStart(2, "0");
        const d = String(val.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
      if (typeof val === "number" && needles.some((n) => n.includes("date"))) {
        const excelEpoch = new Date(1899, 11, 30);
        const date = new Date(excelEpoch.getTime() + val * 86400000);
        if (!isNaN(date.getTime()) && date.getFullYear() > 1970) {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, "0");
          const d = String(date.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        }
      }
      return String(val ?? "").trim();
    }
  }
  return "";
}

function parseNum(row: Record<string, unknown>, needles: string[]): number {
  const str = findCol(row, needles);
  const num = parseFloat(str.replace(/[,$]/g, ""));
  return isNaN(num) ? 0 : num;
}
